import React, { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth';
import { UserProfileResponse } from '../../types';

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString();
};

const ProfilePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await authService.getProfile(30);
        setProfile(response);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };

    void loadProfile();
  }, [isAuthenticated]);

  const recentHistoryCount = useMemo(() => profile?.recent_history.length ?? 0, [profile]);

  if (!isAuthenticated) {
    return (
      <section className="feature-page profile-page">
        <h1>Profile</h1>
        <p>Please sign in to view your profile and history.</p>
      </section>
    );
  }

  return (
    <section className="feature-page profile-page">
      <div className="profile-hero">
        <div className="profile-hero-copy">
          <p className="profile-kicker">Account Center</p>
          <h1>My Profile</h1>
          <p>Track your account details and prediction history in one place.</p>
        </div>

        <div className="profile-hero-metrics" aria-label="Profile snapshot">
          <article className="profile-hero-tile">
            <h3>Total Predictions</h3>
            <p>{profile?.total_predictions ?? 0}</p>
          </article>
          <article className="profile-hero-tile">
            <h3>Recent History</h3>
            <p>{recentHistoryCount}</p>
          </article>
          <article className="profile-hero-tile">
            <h3>Joined</h3>
            <p>{formatDateTime(profile?.created_at ?? null)}</p>
          </article>
          <article className="profile-hero-tile">
            <h3>Last Login</h3>
            <p>{formatDateTime(profile?.last_login_at ?? null)}</p>
          </article>
        </div>
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="panel profile-account-panel">
        <h2>Account Details</h2>
        <div className="profile-account-grid">
          <p><span>Name:</span> {profile?.name || 'N/A'}</p>
          <p><span>Email:</span> {profile?.email || 'N/A'}</p>
          <p><span>Provider:</span> {profile?.auth_provider || 'N/A'}</p>
          <p><span>User ID:</span> {profile?.user_id || 'N/A'}</p>
        </div>
      </div>

      <div className="panel profile-history-panel">
        <h2>My Prediction History</h2>
        {loading ? (
          <p>Loading your history...</p>
        ) : profile?.recent_history.length ? (
          <div className="profile-history-grid">
            {profile.recent_history.map((item) => (
              <article key={item.prediction_id} className="profile-history-item">
                <h3>{item.species}</h3>
                <p><strong>Region:</strong> {item.region}</p>
                <p><strong>Risk:</strong> {item.risk_level}</p>
                <p><strong>Confidence:</strong> {(item.confidence * 100).toFixed(1)}%</p>
                <p>
                  <strong>Coordinates:</strong>{' '}
                  {typeof item.latitude === 'number' && typeof item.longitude === 'number'
                    ? `${item.latitude.toFixed(4)}, ${item.longitude.toFixed(4)}`
                    : 'Not available'}
                </p>
                <p><strong>Timestamp:</strong> {formatDateTime(item.timestamp)}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>You have no saved prediction history yet.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfilePage;
